import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { supabase } from '../supabaseClient';
import WeightBalancePanel from '../WeightBalancePanel';
import wingitDesign from '../designSystem';
import { getShortDocumentTypeLabel } from './documentStatus';
import { saveBookingPreflightState } from './saveBookingPreflightState';

function getSavedPreflight(booking) {
  const saved = booking?.briefing_data?.preflight;

  return saved && typeof saved === 'object'
    ? saved
    : {};
}

export default function PreFlightChecklist({
  nextBooking,
  selectedAircraft,
  cleanIcao,
  isNextBookingWithin24Hours,
  userId,
  organisationId,
  aircraftDocumentStatus,
  isLocalTestSimulation = false,
  onStateChange,
  onCompletePreFlight,
  isReviewMode = false,
}) {
  const ds = wingitDesign;
  const restoreDoneRef = useRef(false);
  const sectionButtonRefs = useRef({});

  const [activeChecklistItem, setActiveChecklistItem] =
    useState('');

  const [weatherReviewed, setWeatherReviewed] =
    useState(false);

  const [notamsReviewed, setNotamsReviewed] =
    useState(false);

  const [routePlanned, setRoutePlanned] =
    useState(false);

  const [
    weightBalanceComplete,
    setWeightBalanceComplete,
  ] = useState(false);
const [
  weightBalanceAcknowledged,
  setWeightBalanceAcknowledged,
] = useState(false);
  const [weightBalanceResult, setWeightBalanceResult] =
    useState({
      configured: false,
      complete: false,
      withinLimits: false,
    });
  const [
    weightBalanceManualAcknowledgement,
    setWeightBalanceManualAcknowledgement,
  ] = useState({
    acknowledged: false,
    acknowledgedAt: null,
    acknowledgedBy: null,
  });
  const [hasGroundingDefect, setHasGroundingDefect] =
    useState(false);
  const [
    completionConfirmed,
    setCompletionConfirmed,
  ] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');
  const [saveError, setSaveError] = useState('');
  const [
    aircraftDocumentAcknowledgements,
    setAircraftDocumentAcknowledgements,
  ] = useState({});
  const [
    simulatedMissingDocumentsAcknowledged,
    setSimulatedMissingDocumentsAcknowledged,
  ] = useState(false);

  const affectedDocumentWarnings = useMemo(() => {
    const rows = Array.isArray(
      aircraftDocumentStatus?.documentRows
    )
      ? aircraftDocumentStatus.documentRows
      : [];

    return rows
      .map((row) => {
        if (!row?.document) {
          return {
            key: String(row.documentType || ''),
            documentType: row.documentType,
            documentName: getShortDocumentTypeLabel(
              row.documentType
            ),
            status: 'missing',
            warningMessage:
              'This required document is missing.',
          };
        }

        if (
          row?.expiryMeta?.status === 'expired'
        ) {
          return {
            key: String(row.documentType || ''),
            documentType: row.documentType,
            documentName: getShortDocumentTypeLabel(
              row.documentType
            ),
            status: 'expired',
            warningMessage: `${getShortDocumentTypeLabel(
              row.documentType
            )} is expired (${row.expiryMeta.label}).`,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [aircraftDocumentStatus]);

  const areAllDocumentWarningsAcknowledged = useMemo(
    () =>
      areDocumentWarningsAcknowledged(
        affectedDocumentWarnings,
        aircraftDocumentAcknowledgements
      ),
    [
      affectedDocumentWarnings,
      aircraftDocumentAcknowledgements,
    ]
  );

  const documentChecklistStatus = useMemo(() => {
    if (!nextBooking) {
      return {
        state: 'unavailable',
        complete: false,
        icon: '○',
        detail:
          'Book an aircraft before reviewing documents.',
      };
    }

    if (aircraftDocumentStatus?.loading) {
      return {
        state: 'loading',
        complete: false,
        icon: '…',
        detail: 'Checking aircraft documents...',
      };
    }

    if (
      affectedDocumentWarnings.length > 0 &&
      isLocalTestSimulation &&
      simulatedMissingDocumentsAcknowledged &&
      !areAllDocumentWarningsAcknowledged
    ) {
      return {
        state: 'simulated',
        complete: true,
        icon: '⚠',
        detail:
          'LOCAL TEST ONLY — missing documents are simulated; none were verified.',
      };
    }

    if (
      affectedDocumentWarnings.length > 0 &&
      !areAllDocumentWarningsAcknowledged
    ) {
      return {
        state: 'problem',
        complete: false,
        icon: '✕',
        detail:
          'Missing or expired documents require acknowledgement before departure.',
      };
    }

    if (
      affectedDocumentWarnings.length > 0 &&
      areAllDocumentWarningsAcknowledged
    ) {
      return {
        state: 'acknowledged',
        complete: true,
        icon: '⚠',
        detail: 'Warnings acknowledged',
      };
    }

    if (
      aircraftDocumentStatus?.overall === 'warning'
    ) {
      return {
        state: 'warning',
        complete: false,
        icon: '⚠',
        detail:
          'One or more documents are expiring soon.',
      };
    }

    return {
      state: 'current',
      complete: true,
      icon: '✓',
      detail:
        'Required documents are available and current.',
    };
  }, [
    nextBooking,
    aircraftDocumentStatus,
    affectedDocumentWarnings,
    areAllDocumentWarningsAcknowledged,
    isLocalTestSimulation,
    simulatedMissingDocumentsAcknowledged,
  ]);

  const isWeightBalanceManuallyAcknowledged =
    weightBalanceManualAcknowledgement
      ?.acknowledged === true;

  const aircraftStatusValue = String(
    selectedAircraft?.status || ''
  )
    .trim()
    .toLowerCase();

  const isAircraftGroundedByStatus =
    aircraftStatusValue === 'aog' ||
    aircraftStatusValue === 'defect' ||
    aircraftStatusValue === 'unserviceable';

  const isAircraftGrounded =
    isAircraftGroundedByStatus ||
    hasGroundingDefect;

  const requiresOwnerConfigurationAttention =
    Boolean(
      nextBooking &&
        (!weightBalanceResult.configured ||
          affectedDocumentWarnings.length > 0)
    );

  const completionRequirementsMet = useMemo(
    () =>
      Boolean(
        nextBooking &&
          isNextBookingWithin24Hours &&
          !isAircraftGrounded &&
          weatherReviewed &&
          notamsReviewed &&
          routePlanned &&
          weightBalanceComplete &&
          documentChecklistStatus.complete
      ),
    [
      nextBooking,
      isNextBookingWithin24Hours,
      isAircraftGrounded,
      weatherReviewed,
      notamsReviewed,
      routePlanned,
      weightBalanceComplete,
      documentChecklistStatus.complete,
    ]
  );

  const planningComplete =
    completionRequirementsMet &&
    completionConfirmed;
  const isCompletedReviewMode =
    isReviewMode && completionConfirmed;

  const checklistItems = useMemo(
    () => [
      {
        id: 'weather',
        title: weatherReviewed
          ? 'Weather reviewed'
          : 'Review weather',
        complete: weatherReviewed,
        disabled:
          !nextBooking ||
          !isNextBookingWithin24Hours,
        unavailableText:
          'Available within 24 hours of departure.',
      },
      {
        id: 'notams',
        title: notamsReviewed
          ? 'NOTAMs reviewed'
          : 'Review NOTAMs',
        complete: notamsReviewed,
        disabled:
          !nextBooking ||
          !isNextBookingWithin24Hours,
        unavailableText:
          'Available within 24 hours of departure.',
      },
      {
        id: 'route',
        title: routePlanned
          ? 'Route planning complete'
          : 'Confirm route planning',
        complete: routePlanned,
        disabled: !nextBooking,
        unavailableText:
          'Book an aircraft before completing route planning.',
      },
      {
        id: 'weightBalance',
        title: isWeightBalanceManuallyAcknowledged
          ? 'Manual calculation acknowledged'
          : weightBalanceComplete
          ? 'Weight & Balance complete'
          : 'Complete Weight & Balance',
        complete: weightBalanceComplete,
        disabled: !nextBooking,
        unavailableText:
          'Book an aircraft before completing weight & balance.',
      },
      {
        id: 'documents',
        title:
          documentChecklistStatus.state ===
          'acknowledged'
            ? 'Warnings acknowledged'
            : documentChecklistStatus.state ===
                'simulated'
              ? 'Synthetic document simulation'
            : 'Aircraft Documents',
        complete: documentChecklistStatus.complete,
        disabled: !nextBooking,
        unavailableText:
          'Book an aircraft before reviewing documents.',
        statusState: documentChecklistStatus.state,
        statusIcon: documentChecklistStatus.icon,
        statusDetail: documentChecklistStatus.detail,
      },
    ],
    [
      weatherReviewed,
      notamsReviewed,
      routePlanned,
      weightBalanceComplete,
      isWeightBalanceManuallyAcknowledged,
      nextBooking,
      isNextBookingWithin24Hours,
      documentChecklistStatus,
    ]
  );

  useEffect(() => {
    restoreDoneRef.current = false;

    const saved = getSavedPreflight(nextBooking);

    setWeatherReviewed(
      Boolean(saved.weatherReviewed)
    );

    setNotamsReviewed(
      Boolean(saved.notamsReviewed)
    );

    setRoutePlanned(
      Boolean(saved.routePlanned)
    );

    setWeightBalanceComplete(
  Boolean(saved.weightBalanceComplete)
);

setWeightBalanceAcknowledged(
  Boolean(saved.weightBalanceAcknowledged)
);
    setWeightBalanceManualAcknowledgement(
      saved.weightBalanceManualAcknowledgement &&
        typeof saved.weightBalanceManualAcknowledgement ===
          'object'
        ? {
            acknowledged:
              saved
                .weightBalanceManualAcknowledgement
                .acknowledged === true,
            acknowledgedAt:
              saved
                .weightBalanceManualAcknowledgement
                .acknowledgedAt || null,
            acknowledgedBy:
              saved
                .weightBalanceManualAcknowledgement
                .acknowledgedBy || null,
          }
        : {
            acknowledged: false,
            acknowledgedAt: null,
            acknowledgedBy: null,
          }
    );
    setAircraftDocumentAcknowledgements(
      saved.aircraftDocumentAcknowledgements &&
        typeof saved.aircraftDocumentAcknowledgements ===
          'object'
        ? saved.aircraftDocumentAcknowledgements
        : {}
    );
    setSimulatedMissingDocumentsAcknowledged(
      isLocalTestSimulation &&
        saved.simulatedMissingDocumentsAcknowledged === true
    );
    setCompletionConfirmed(
      (
        saved.completed === true ||
        saved.completionConfirmed === true
      ) &&
        (
          saved.simulationOnly !== true ||
          isLocalTestSimulation
        )
    );

    setActiveChecklistItem('');
    setSaveNotice('');
    setSaveError('');

    restoreDoneRef.current = true;
  }, [
    nextBooking,
    isCompletedReviewMode,
    isLocalTestSimulation,
  ]);

  useEffect(() => {
    if (typeof onStateChange !== 'function') {
      return;
    }

    onStateChange({
      planningComplete,
      weatherReviewed,
      notamsReviewed,
      routePlanned,
      weightBalanceComplete,
      documentsSatisfied:
        documentChecklistStatus.complete,
      blockedByGrounding:
        isAircraftGrounded,
    });
  }, [
    onStateChange,
    planningComplete,
    weatherReviewed,
    notamsReviewed,
    routePlanned,
    weightBalanceComplete,
    documentChecklistStatus.complete,
    isAircraftGrounded,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function checkGroundingDefect() {
      if (!nextBooking?.aircraft_id) {
        setHasGroundingDefect(false);
        return;
      }

      const { data, error } = await supabase
        .from('aircraft_defects')
        .select('id')
        .eq('aircraft_id', nextBooking.aircraft_id)
        .eq('severity', 'safety_critical')
        .neq('status', 'rectified')
        .limit(1);

      if (cancelled || error) {
        return;
      }

      setHasGroundingDefect(
        Array.isArray(data) && data.length > 0
      );
    }

    checkGroundingDefect();

    return () => {
      cancelled = true;
    };
  }, [nextBooking?.aircraft_id]);

  function buildPersistState(overrides = {}) {
    return {
      weatherReviewed,
      notamsReviewed,
      routePlanned,
      weightBalanceComplete,
      weightBalanceAcknowledged,
      weightBalanceManualAcknowledgement,
      aircraftDocumentAcknowledgements,
      documentsCurrent:
        documentChecklistStatus.complete &&
        documentChecklistStatus.state !== 'simulated',
      simulatedMissingDocumentsAcknowledged:
        isLocalTestSimulation &&
        simulatedMissingDocumentsAcknowledged,
      completionConfirmed,
      ...overrides,
    };
  }

  const savePreflightState = useCallback(
    async (nextState) => {
      if (
        !nextBooking?.id ||
        !userId ||
        !restoreDoneRef.current
      ) {
        return;
      }

      setSaving(true);
      setSaveNotice('');
      setSaveError('');

      const now = new Date().toISOString();

      const existingPreflight =
        getSavedPreflight(nextBooking);

      const documentsSatisfied = Boolean(
        nextState.documentsCurrent === true ||
          (
            isLocalTestSimulation &&
            nextState.simulatedMissingDocumentsAcknowledged === true
          )
      );

      const completed = Boolean(
        nextState.completionConfirmed === true &&
        !isAircraftGrounded &&
        nextState.weatherReviewed &&
          nextState.notamsReviewed &&
          nextState.routePlanned &&
          nextState.weightBalanceComplete &&
          documentsSatisfied
      );

      const preflight = {
        ...existingPreflight,
        completed,
        weatherReviewed: Boolean(
          nextState.weatherReviewed
        ),
        notamsReviewed: Boolean(
          nextState.notamsReviewed
        ),
        routePlanned: Boolean(
          nextState.routePlanned
        ),
        weightBalanceComplete: Boolean(
          nextState.weightBalanceComplete
        ),
        weightBalanceAcknowledged: Boolean(
  nextState.weightBalanceAcknowledged
),
        weightBalanceManualAcknowledgement:
          nextState.weightBalanceManualAcknowledgement &&
          typeof nextState.weightBalanceManualAcknowledgement ===
            'object'
            ? nextState.weightBalanceManualAcknowledgement
            : {
                acknowledged: false,
                acknowledgedAt: null,
                acknowledgedBy: null,
              },
        documentsCurrent: Boolean(
          nextState.documentsCurrent
        ),
        simulationOnly:
          isLocalTestSimulation ||
          existingPreflight.simulationOnly === true,
        simulationLabel: isLocalTestSimulation
          ? 'LOCAL TEST ONLY - synthetic software validation; not operational evidence'
          : existingPreflight.simulationLabel || null,
        simulatedMissingDocumentsAcknowledged:
          isLocalTestSimulation &&
          nextState.simulatedMissingDocumentsAcknowledged === true,
        aircraftDocumentAcknowledgements:
          nextState.aircraftDocumentAcknowledgements &&
          typeof nextState.aircraftDocumentAcknowledgements ===
            'object'
            ? nextState.aircraftDocumentAcknowledgements
            : {},
        completionConfirmed:
          nextState.completionConfirmed === true,
        updatedAt: now,
        completedAt: completed
          ? existingPreflight.completedAt || now
          : null,
      };

      const { data, error } =
        await saveBookingPreflightState(
          supabase,
          nextBooking.id,
          preflight
        );

      if (
        error ||
        data?.ok !== true ||
        String(data?.booking_id || '') !==
          String(nextBooking.id)
      ) {
        setSaveError(
          error?.message ||
            data?.message ||
            'Could not save Pre-Flight progress.'
        );
        setSaving(false);
        return false;
      }

      setSaveNotice(
        'Pre-Flight progress saved.'
      );

      setSaving(false);
      return true;
    },
    [
      nextBooking,
      userId,
      isAircraftGrounded,
      isLocalTestSimulation,
    ]
  );

  useEffect(() => {
    if (
      !restoreDoneRef.current ||
      !nextBooking?.id ||
      !completionRequirementsMet ||
      completionConfirmed
    ) {
      return;
    }

    let cancelled = false;

    async function autoCompletePreflight() {
      const saved = await savePreflightState({
        weatherReviewed,
        notamsReviewed,
        routePlanned,
        weightBalanceComplete,
        weightBalanceAcknowledged,
        weightBalanceManualAcknowledgement,
        aircraftDocumentAcknowledgements,
        simulatedMissingDocumentsAcknowledged,
        documentsCurrent:
          documentChecklistStatus.complete &&
          documentChecklistStatus.state !== 'simulated',
        completionConfirmed: true,
      });

      if (!saved || cancelled) {
        return;
      }

      setCompletionConfirmed(true);
      setSaveNotice('Pre-Flight completed.');
    }

    autoCompletePreflight();

    return () => {
      cancelled = true;
    };
  }, [
    nextBooking?.id,
    completionRequirementsMet,
    completionConfirmed,
    savePreflightState,
    weatherReviewed,
    notamsReviewed,
    routePlanned,
    weightBalanceComplete,
    weightBalanceAcknowledged,
    weightBalanceManualAcknowledgement,
    aircraftDocumentAcknowledgements,
    simulatedMissingDocumentsAcknowledged,
    documentChecklistStatus.complete,
    documentChecklistStatus.state,
  ]);

  async function ensureOwnerDocumentAction(warning) {
    if (!nextBooking?.id || !nextBooking?.aircraft_id) {
      return;
    }

    const actionKey = getDocumentActionKey(
      nextBooking.id,
      warning.documentType
    );

    const { data: existingRows } = await supabase
      .from('aircraft_defects')
      .select('id, status, details')
      .eq('booking_id', nextBooking.id)
      .eq('aircraft_id', nextBooking.aircraft_id)
      .neq('status', 'rectified')
      .ilike('details', `${actionKey}%`)
      .limit(1);

    if (Array.isArray(existingRows) && existingRows.length > 0) {
      return;
    }

    const acknowledgedAt = new Date().toISOString();
    const payload = {
      actionType: 'aircraft_document_acknowledgement',
      organisationId: String(organisationId || ''),
      aircraftId: String(nextBooking.aircraft_id || ''),
      bookingId: String(nextBooking.id || ''),
      pilotId: String(userId || ''),
      documentType: warning.documentType,
      documentName: warning.documentName,
      status: warning.status,
      bookingDate: String(nextBooking.date || ''),
      acknowledgedAt,
      unresolved: true,
    };

    await supabase.from('aircraft_defects').insert([
      {
        aircraft_id: nextBooking.aircraft_id,
        booking_id: nextBooking.id,
        reported_by: userId || null,
        severity: 'warning',
        status: 'open',
        details: `${actionKey}|${JSON.stringify(payload)}`,
      },
    ]);
  }

  async function ensureOwnerWeightBalanceAction() {
    if (!nextBooking?.id || !nextBooking?.aircraft_id) {
      return;
    }

    const actionKey = getWeightBalanceActionKey(
      nextBooking.id,
      nextBooking.aircraft_id
    );

    const { data: existingRows } = await supabase
      .from('aircraft_defects')
      .select('id, status, details')
      .eq('booking_id', nextBooking.id)
      .eq('aircraft_id', nextBooking.aircraft_id)
      .neq('status', 'rectified')
      .ilike('details', `${actionKey}%`)
      .limit(1);

    if (
      Array.isArray(existingRows) &&
      existingRows.length > 0
    ) {
      return;
    }

    const acknowledgedAt = new Date().toISOString();

    const payload = {
      actionType:
        'aircraft_weight_balance_configuration',
      organisationId: String(organisationId || ''),
      aircraftId: String(nextBooking.aircraft_id || ''),
      bookingId: String(nextBooking.id || ''),
      pilotId: String(userId || ''),
      bookingDate: String(nextBooking.date || ''),
      acknowledgedAt,
      unresolved: true,
    };

    await supabase.from('aircraft_defects').insert([
      {
        aircraft_id: nextBooking.aircraft_id,
        booking_id: nextBooking.id,
        reported_by: userId || null,
        severity: 'warning',
        status: 'open',
        details: `${actionKey}|${JSON.stringify(payload)}`,
      },
    ]);
  }

  async function applyDocumentAcknowledgement(
    warning,
    checked
  ) {
    if (!warning?.key) return false;

    const now = new Date().toISOString();

    const nextAcknowledgements = {
      ...aircraftDocumentAcknowledgements,
      [warning.key]: {
        acknowledged: checked,
        acknowledgedAt: checked ? now : null,
        acknowledgedBy: checked ? userId || null : null,
        status: warning.status,
        documentName: warning.documentName,
      },
    };

    const documentsCurrent = areDocumentWarningsAcknowledged(
      affectedDocumentWarnings,
      nextAcknowledgements
    );

    const saved = await savePreflightState(
      buildPersistState({
        aircraftDocumentAcknowledgements:
          nextAcknowledgements,
        documentsCurrent,
        completionConfirmed: false,
      })
    );

    if (!saved) {
      return false;
    }

    setAircraftDocumentAcknowledgements(nextAcknowledgements);

    if (checked && documentsCurrent) {
      setActiveChecklistItem('');
    }

    if (checked) {
      ensureOwnerDocumentAction(warning);
    }

    return true;
  }

  function handleDocumentAcknowledgementToggle(
    warning,
    checked
  ) {
    if (!warning?.key) return;

    void applyDocumentAcknowledgement(warning, checked);
  }

  async function applyManualWeightBalanceAcknowledgement(
    checked
  ) {
    const now = new Date().toISOString();
    const manualAcknowledgement = {
      acknowledged: checked,
      acknowledgedAt: checked ? now : null,
      acknowledgedBy: checked ? userId || null : null,
    };

    const saved = await savePreflightState(
      buildPersistState({
        weightBalanceComplete: checked,
        weightBalanceAcknowledged: checked,
        weightBalanceManualAcknowledgement:
          manualAcknowledgement,
        completionConfirmed: false,
      })
    );

    if (!saved) {
      return;
    }

    setWeightBalanceManualAcknowledgement(
      manualAcknowledgement
    );
    setWeightBalanceAcknowledged(checked);
    setWeightBalanceComplete(checked);

    if (checked) {
      setActiveChecklistItem('');
      ensureOwnerWeightBalanceAction();
    }
  }

  function handleManualWeightBalanceAcknowledgement(
    checked
  ) {
    if (checked && isAircraftGrounded) {
      setSaveError(
        'Pre-Flight cannot proceed while this aircraft is grounded or unserviceable.'
      );
      return;
    }

    if (checked) {
      void applyManualWeightBalanceAcknowledgement(true);
      return;
    }

    void applyManualWeightBalanceAcknowledgement(false);
  }

  function openExternal(url) {
    window.open(
      url,
      '_blank',
      'noopener,noreferrer'
    );
  }

  function openNotamLookup() {
    openExternal(
      'https://nats-uk.ead-it.com/fwf-nats/public/cms/cmscontent.faces?configKey=default.home.page'
    );
  }

  function openWeatherLookup() {
    openExternal(
      `https://metar-taf.com/${encodeURIComponent(
        cleanIcao
      )}`
    );
  }

  function openWindMap() {
    openExternal(
      'https://login.auth.metoffice.cloud/dce84ec6-ce0f-45d1-ba16-e36b817081eb/oauth2/v2.0/authorize?p=b2c_1a_warrior_susi&response_type=code&client_id=587d94f9-ffb2-4804-9389-e9d28de2972b&group_prefixes_filter=b2c%2FM&response_mode=form_post&scope=openid%20offline_access&redirect_uri=https%3A%2F%2Fwww.mavis.metoffice.gov.uk%2F_callback&nonce=178055625027600&state=%2F'
    );
  }

  function openSkyDemon() {
    openExternal('https://www.skydemon.aero/');
  }

  function openForeFlight() {
    openExternal('https://foreflight.com/');
  }

  function openGarminPilot() {
    openExternal(
      'https://www.garmin.com/en-US/aviation/garminpilot/overview/'
    );
  }

async function markWeatherReviewed() {
  const nextState = buildPersistState({
    weatherReviewed: true,
    completionConfirmed: false,
  });

  const saved = await savePreflightState(nextState);

  if (!saved) {
    return;
  }

  setWeatherReviewed(true);
  setActiveChecklistItem('');
  }

  async function markNotamsReviewed() {
    const nextState = buildPersistState({
      notamsReviewed: true,
      completionConfirmed: false,
    });

    const saved = await savePreflightState(nextState);

    if (!saved) {
      return;
    }

    setNotamsReviewed(true);
    setActiveChecklistItem('');
  }

  async function markRoutePlanned() {
    const nextState = buildPersistState({
      routePlanned: true,
      completionConfirmed: false,
    });

    const saved = await savePreflightState(nextState);

    if (!saved) {
      return;
    }

    setRoutePlanned(true);
    setActiveChecklistItem('');
  }

  async function recordSyntheticMissingDocuments() {
    if (
      !isLocalTestSimulation ||
      affectedDocumentWarnings.length === 0
    ) {
      return;
    }

    const nextState = buildPersistState({
      documentsCurrent: false,
      simulatedMissingDocumentsAcknowledged: true,
      completionConfirmed: false,
    });

    const saved = await savePreflightState(nextState);

    if (!saved) {
      return;
    }

    setSimulatedMissingDocumentsAcknowledged(true);
    setActiveChecklistItem('');
  }

  async function markWeightBalanceComplete() {
    if (isAircraftGrounded) {
      setSaveError(
        'Pre-Flight cannot proceed while this aircraft is grounded or unserviceable.'
      );
      return;
    }

    if (!weightBalanceResult.configured) {
      setSaveError(
        'Weight & Balance is not configured for this aircraft.'
      );
      return;
    }

    if (!weightBalanceResult.complete) {
      setSaveError(
        'Complete all Weight & Balance loading fields first.'
      );
      return;
    }

    if (!weightBalanceResult.withinLimits) {
      setSaveError(
        'Weight & Balance is outside limits and cannot be marked complete.'
      );
      return;
    }

    const nextState = buildPersistState({
      weightBalanceComplete: true,
      weightBalanceAcknowledged: true,
      weightBalanceManualAcknowledgement: {
        acknowledged: false,
        acknowledgedAt: null,
        acknowledgedBy: null,
      },
      completionConfirmed: false,
    });

    const saved = await savePreflightState(nextState);

    if (!saved) {
      return;
    }

    setWeightBalanceComplete(true);
    setWeightBalanceAcknowledged(true);
    setActiveChecklistItem('');
  }

  function isCompletedReviewableSection(item) {
    return Boolean(
      item?.complete &&
        [
          'weather',
          'notams',
          'route',
          'weightBalance',
          'documents',
        ].includes(
          item?.id
        )
    );
  }

  function handleSectionReviewToggle(sectionId) {
    setActiveChecklistItem(
      activeChecklistItem === sectionId
        ? ''
        : sectionId
    );
  }

  function handleRouteToolClick(openTool) {
    openTool();
  }

  function getChecklistIcon(item) {
    if (
      item.id === 'weightBalance' &&
      isWeightBalanceManuallyAcknowledged
    ) {
      return '⚠';
    }

    if (item.id === 'documents') {
      return item.statusIcon;
    }

    return item.complete ? '✓' : '○';
  }

  function getChecklistIconStyle(item) {
    if (
      item.id === 'weightBalance' &&
      isWeightBalanceManuallyAcknowledged
    ) {
      return styles.checkIconWarning;
    }

    if (item.id !== 'documents') {
      return item.complete
        ? styles.checkIconComplete
        : styles.checkIconIncomplete;
    }

    if (item.statusState === 'problem') {
      return styles.checkIconProblem;
    }

    if (item.statusState === 'warning') {
      return styles.checkIconWarning;
    }

    if (item.statusState === 'acknowledged') {
      return styles.checkIconWarning;
    }

    if (item.statusState === 'simulated') {
      return styles.checkIconWarning;
    }

    if (item.statusState === 'current') {
      return styles.checkIconComplete;
    }

    return styles.checkIconIncomplete;
  }

  function getChecklistDetail(item) {
    if (item.disabled) {
      return item.unavailableText;
    }

    if (
      item.id === 'weightBalance' &&
      isWeightBalanceManuallyAcknowledged
    ) {
      return 'Manual calculation acknowledged';
    }

    if (item.id === 'documents') {
      return item.statusDetail;
    }

    if (item.complete) {
      return 'Complete';
    }

    return 'Open and complete';
  }

  function handleReviewSection(sectionId) {
    setActiveChecklistItem(sectionId);

    const sectionElement =
      sectionButtonRefs.current[sectionId];

    if (sectionElement) {
      sectionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  function renderWeatherTools() {
    return (
      <>
        <div style={styles.actionRow}>
          <button
            type="button"
            onClick={openWeatherLookup}
            style={ds.buttons.secondary}
          >
            Local Weather
          </button>

          <button
            type="button"
            onClick={openWindMap}
            style={ds.buttons.secondary}
          >
            MAVIS
          </button>
        </div>
      </>
    );
  }

  function renderNotamTools() {
    return (
      <>
        <div style={styles.actionRow}>
          <button
            type="button"
            onClick={openNotamLookup}
            style={ds.buttons.secondary}
          >
            Open NOTAM Lookup
          </button>
        </div>
      </>
    );
  }

  function renderRouteTools() {
    return (
      <>
        <div style={styles.actionRow}>
          <button
            type="button"
            onClick={() =>
              handleRouteToolClick(
                openSkyDemon
              )
            }
            style={ds.buttons.secondary}
          >
            SkyDemon
          </button>

          <button
            type="button"
            onClick={() =>
              handleRouteToolClick(
                openForeFlight
              )
            }
            style={ds.buttons.secondary}
          >
            ForeFlight
          </button>

          <button
            type="button"
            onClick={() =>
              handleRouteToolClick(
                openGarminPilot
              )
            }
            style={ds.buttons.secondary}
          >
            Garmin Pilot
          </button>
        </div>
      </>
    );
  }

  function handleClosePreFlight() {
    if (typeof onCompletePreFlight === 'function') {
      onCompletePreFlight();
    }
  }

  return (
    <>
    <section className="pilot-pre-flight__checklist" style={ds.cards.section}>
      <div className="pilot-pre-flight__checklist-header" style={styles.checklistHeader}>
        <div>
          <div style={styles.kicker}>
            PRE-FLIGHT CHECKLIST
          </div>

          <h3 style={styles.title}>
            Complete before departure
          </h3>
        </div>

        <span
          style={
            planningComplete
              ? styles.statusPillReady
              : styles.statusPillIncomplete
          }
        >
          <span
            style={
              planningComplete
                ? styles.statusDotReady
                : styles.statusDotIncomplete
            }
          />

          {planningComplete
            ? 'Ready'
            : 'Incomplete'}
        </span>
      </div>

      {requiresOwnerConfigurationAttention ? (
        <div style={styles.ownerActionBanner}>
          <div style={styles.ownerActionBannerTitle}>
            OWNER ACTION REQUIRED
          </div>

          <div style={styles.ownerActionBannerText}>
            This aircraft has configuration items that require the owner's attention. Continue only when appropriate alternative checks have been completed.
          </div>
        </div>
      ) : null}

      {isAircraftGrounded ? (
        <div style={styles.groundingBanner}>
          Aircraft is currently grounded or unserviceable. Pre-Flight cannot be completed until this is resolved.
        </div>
      ) : null}

      {saving ? (
        <div style={styles.saveText}>
          Saving...
        </div>
      ) : null}

      {saveNotice ? (
        <div style={styles.saveText}>
          {saveNotice}
        </div>
      ) : null}

      {saveError ? (
        <div style={styles.errorText}>
          {saveError}
        </div>
      ) : null}

      <div style={styles.checklistList}>
        {checklistItems.map((item) => (
          <div key={item.id}>
            {(() => {
              const completedReviewable =
                item.id === 'documents'
                  ? activeChecklistItem === 'documents' ||
                    documentChecklistStatus.complete
                  : isCompletedReviewableSection(item);
              const rowDisabled =
                item.disabled ||
                isCompletedReviewMode ||
                completedReviewable;
              const showActionButton = true;

              const isReviewAction =
                item.id === 'documents' ||
                isCompletedReviewMode ||
                completedReviewable;

              const actionButtonLabel =
                activeChecklistItem === item.id
                  ? 'Collapse'
                  : item.id === 'weightBalance'
                  ? 'Review'
                  : isReviewAction
                  ? 'Review'
                  : 'Open';

              return (
            <div
              className="pilot-pre-flight__checklist-row"
              ref={(element) => {
                sectionButtonRefs.current[item.id] =
                  element;
              }}
              style={{
                ...styles.checklistButton,
                ...(activeChecklistItem === item.id
                  ? styles.checklistButtonOpen
                  : {}),
                opacity: item.disabled ? 0.55 : 1,
                cursor:
                  showActionButton
                    ? 'default'
                    : rowDisabled
                    ? 'not-allowed'
                    : 'pointer',
              }}
            >
              <span
                style={getChecklistIconStyle(item)}
              >
                {getChecklistIcon(item)}
              </span>

              <span style={styles.checkText}>
                <span style={styles.checkTitle}>
                  {item.title}
                </span>

                <span style={styles.checkDetail}>
                  {getChecklistDetail(item)}
                </span>
              </span>

              {showActionButton ? (
                <button
                  type="button"
                  onClick={() => {
                    if (activeChecklistItem === item.id) {
                      handleSectionReviewToggle(item.id);
                      return;
                    }

                    if (isReviewAction) {
                      handleReviewSection(item.id);
                      return;
                    }

                    handleSectionReviewToggle(item.id);
                  }}
                  disabled={item.disabled}
                  style={styles.reviewButton}
                >
                  {actionButtonLabel}
                </button>
              ) : null}
            </div>
              );
            })()}

            {item.id === 'weather' && activeChecklistItem === 'weather' ? (
              <div style={styles.expandedArea}>
                <ChecklistPanel
                  title="Weather"
                  description={`Review the latest weather information for ${cleanIcao}.`}
                  complete={weatherReviewed}
                  onConfirm={markWeatherReviewed}
                  confirmLabel="Confirm weather reviewed"
                  hideConfirm={isCompletedReviewMode}
                >
                  {renderWeatherTools()}
                </ChecklistPanel>
              </div>
            ) : null}

            {item.id === 'notams' && activeChecklistItem === 'notams' ? (
              <div style={styles.expandedArea}>
                <ChecklistPanel
                  title="NOTAMs"
                  description="Review current NOTAMs for the route and departure area, then confirm they have been checked."
                  complete={notamsReviewed}
                  onConfirm={markNotamsReviewed}
                  confirmLabel="Confirm NOTAMs reviewed"
                  hideConfirm={isCompletedReviewMode}
                >
                  {renderNotamTools()}
                </ChecklistPanel>
              </div>
            ) : null}

            {item.id === 'route' && activeChecklistItem === 'route' ? (
              <div style={styles.expandedArea}>
                <ChecklistPanel
                  title="Route planning"
                  description="Open your preferred route-planning tool, then confirm the route is planned."
                  complete={routePlanned}
                  onConfirm={markRoutePlanned}
                  confirmLabel="Confirm route planned"
                  hideConfirm={isCompletedReviewMode}
                >
                  {renderRouteTools()}
                </ChecklistPanel>
              </div>
            ) : null}

            {item.id === 'weightBalance' && activeChecklistItem === 'weightBalance' ? (
              <div style={styles.expandedArea}>
                <ChecklistPanel
                  title="Weight & Balance"
                  description="Enter the flight loading. Wingit will mark this complete automatically when the aircraft is within limits."
                  complete={weightBalanceComplete}
                  statusLabel={
                    isWeightBalanceManuallyAcknowledged
                      ? 'Manual calculation acknowledged'
                      : null
                  }
                  statusTone={
                    isWeightBalanceManuallyAcknowledged
                      ? 'warning'
                      : null
                  }
                  onConfirm={
                    markWeightBalanceComplete
                  }
                  confirmLabel="Confirm Weight & Balance PASS"
                  confirmDisabled={
                    !weightBalanceResult.configured ||
                    !weightBalanceResult.complete ||
                    !weightBalanceResult.withinLimits
                  }
                  hideConfirm
                >
                  {!nextBooking ? (
                    <div style={styles.muted}>
                      Not required unless aircraft booked.
                    </div>
                  ) : selectedAircraft ? (
                    <>
                      <WeightBalancePanel
                        aircraft={selectedAircraft}
                        bookingDate={nextBooking?.date || ''}
                        compact
                        onResultChange={setWeightBalanceResult}
                        onSave={
                          isCompletedReviewMode
                            ? () => {}
                            : markWeightBalanceComplete
                        }
                      />

                      {!weightBalanceResult.configured ? (
                        <label style={styles.documentAckLabel}>
                          <input
                            type="checkbox"
                            checked={
                              isWeightBalanceManuallyAcknowledged
                            }
                            disabled={
                              isAircraftGrounded ||
                              isCompletedReviewMode
                            }
                            onChange={(event) =>
                              handleManualWeightBalanceAcknowledgement(
                                event.target.checked
                              )
                            }
                          />

                          <span>
                            I have completed a manual Weight & Balance calculation outside Wingit.
                          </span>
                        </label>
                      ) : null}
                    </>
                  ) : (
                    <div style={styles.muted}>
                      Add/select an aircraft to use W&amp;B auto-fill.
                    </div>
                  )}
                </ChecklistPanel>
              </div>
            ) : null}

            {item.id === 'documents' && activeChecklistItem === 'documents' ? (
              <div style={styles.expandedArea}>
                <ChecklistPanel
                  title="Aircraft Documents"
                  description="Review each missing or expired required document warning before flight."
                  complete={
                    documentChecklistStatus.complete
                  }
                  statusLabel={
                    documentChecklistStatus.state ===
                    'acknowledged'
                      ? 'Warnings acknowledged'
                      : documentChecklistStatus.state ===
                          'simulated'
                        ? 'Synthetic simulation recorded'
                      : null
                  }
                  statusTone={
                    [
                      'acknowledged',
                      'simulated',
                    ].includes(documentChecklistStatus.state)
                      ? 'warning'
                      : null
                  }
                  hideConfirm
                >
                  <div style={styles.muted}>
                    {documentChecklistStatus.detail}
                  </div>

                  {isLocalTestSimulation &&
                  affectedDocumentWarnings.length > 0 ? (
                    <div
                      style={{
                        margin: '0.75rem 0',
                        padding: '0.75rem',
                        border: '1px solid #ffcc66',
                        borderRadius: 8,
                        color: '#ffe0a3',
                        background: 'rgba(184, 139, 42, 0.12)',
                      }}
                    >
                      <div>
                        Local development simulation only. This does
                        not verify or acknowledge any real aircraft
                        document.
                      </div>
                      <button
                        type="button"
                        onClick={recordSyntheticMissingDocuments}
                        disabled={
                          saving ||
                          isAircraftGrounded ||
                          isCompletedReviewMode
                        }
                        style={{
                          ...ds.buttons.secondary,
                          marginTop: '0.65rem',
                        }}
                      >
                        Record synthetic missing-document state
                      </button>
                    </div>
                  ) : null}

                  {affectedDocumentWarnings.length > 0 ? (
                    <div style={styles.documentWarningList}>
                      {affectedDocumentWarnings.map(
                        (warning) => {
                          const acknowledgement =
                            aircraftDocumentAcknowledgements[
                              warning.key
                            ] || {};
                          const isAcknowledged =
                            acknowledgement.acknowledged ===
                            true;

                          return (
                          <div
                            key={warning.key}
                            style={
                              isAcknowledged
                                ? styles.documentAcknowledged
                                : styles.documentProblem
                            }
                          >
                            <div style={styles.documentWarningTitle}>
                              {warning.documentName} ·{' '}
                              {warning.status === 'missing'
                                ? 'Missing'
                                : 'Expired'}
                            </div>

                            <div style={styles.documentWarningMessage}>
                              ⚠ {warning.warningMessage}
                            </div>

                            <label style={styles.documentAckLabel}>
                              <input
                                type="checkbox"
                                checked={isAcknowledged}
                                disabled={
                                  isAircraftGrounded ||
                                  isCompletedReviewMode
                                }
                                onChange={(event) =>
                                  handleDocumentAcknowledgementToggle(
                                    warning,
                                    event.target.checked
                                  )
                                }
                              />

                              <span>
                                {warning.status ===
                                'missing'
                                  ? 'I have verified this document outside Wingit.'
                                  : 'I have confirmed that the document is valid or has been renewed outside Wingit.'}
                              </span>
                            </label>
                          </div>
                        );
                        }
                      )}
                    </div>
                  ) : null}

                  <div style={styles.muted}>
                    Use the Aircraft Documents card below to open the stored files.
                  </div>
                </ChecklistPanel>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="pilot-pre-flight__actions" style={styles.completeActionRow}>
        <button
          type="button"
          onClick={handleClosePreFlight}
          style={{
            ...ds.buttons.primary,
            opacity: 1,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </section>
    </>
  );
}

function ChecklistPanel({
  title,
  description,
  children,
  complete,
  statusLabel = null,
  statusTone = null,
  onConfirm,
  confirmLabel,
  confirmDisabled = false,
  hideConfirm = false,
}) {
  const ds = wingitDesign;

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <div style={styles.kicker}>
            {title}
          </div>

          <h4 style={styles.panelTitle}>
            {statusLabel ||
              (complete
                ? 'Complete'
                : 'Review required')}
          </h4>

          {statusTone === 'warning' ? (
            <div style={styles.panelAcknowledgedPill}>
              ACKNOWLEDGED
            </div>
          ) : null}

          <p style={styles.panelDescription}>
            {description}
          </p>
        </div>
      </div>

      <div style={styles.panelBody}>
        {children}
      </div>

      {!hideConfirm ? (
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirmDisabled}
          style={{
            ...ds.buttons.primary,
            opacity: confirmDisabled ? 0.55 : 1,
            cursor: confirmDisabled
              ? 'not-allowed'
              : 'pointer',
          }}
        >
          {complete
            ? 'Confirmed'
            : confirmLabel}
        </button>
      ) : null}
    </div>
  );
}

const styles = {
  checklistHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    marginBottom: '1rem',
  },

  kicker: {
    color: '#55c7ff',
    fontSize: '0.72rem',
    fontWeight: 900,
    letterSpacing: '0.11em',
    textTransform: 'uppercase',
  },

  title: {
    color: '#f3f8ff',
    margin: '0.25rem 0 0',
    fontSize: '1.15rem',
    fontWeight: 850,
  },

  statusPillReady: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: '#8cff8c',
    background: 'rgba(140,255,140,0.10)',
    border:
      '1px solid rgba(140,255,140,0.24)',
    borderRadius: 999,
    padding: '0.28rem 0.6rem',
    fontSize: '0.74rem',
    fontWeight: 850,
    lineHeight: 1,
  },

  statusPillIncomplete: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: '#ffd27a',
    background: 'rgba(255,210,122,0.08)',
    border:
      '1px solid rgba(255,210,122,0.22)',
    borderRadius: 999,
    padding: '0.28rem 0.6rem',
    fontSize: '0.74rem',
    fontWeight: 850,
    lineHeight: 1,
  },

  statusDotReady: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#8cff8c',
    boxShadow:
      '0 0 10px rgba(140,255,140,0.45)',
  },

  statusDotIncomplete: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: '#ffd27a',
    boxShadow:
      '0 0 10px rgba(255,210,122,0.35)',
  },

  saveText: {
    color: '#8db7e6',
    fontSize: '0.78rem',
    fontWeight: 700,
    marginBottom: '0.7rem',
  },

  errorText: {
    color: '#ff9c9c',
    fontSize: '0.78rem',
    fontWeight: 700,
    marginBottom: '0.7rem',
  },

  checklistList: {
    display: 'grid',
    gap: '0.55rem',
  },

  checklistButton: {
    width: '100%',
    boxSizing: 'border-box',
    background: 'rgba(255,255,255,0.035)',
    border:
      '1px solid rgba(120,180,255,0.14)',
    borderRadius: 14,
    padding: '0.75rem 0.85rem',
    display: 'grid',
    gridTemplateColumns: '28px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: '0.7rem',
    minHeight: 62,
    textAlign: 'left',
    color: '#d7ebff',
  },

  reviewButton: {
    marginTop: 0,
    justifySelf: 'end',
    alignSelf: 'center',
    whiteSpace: 'nowrap',
    background: 'rgba(85,199,255,0.08)',
    color: '#dff5ff',
    border: '1px solid rgba(85,199,255,0.28)',
    borderRadius: 999,
    padding: '0.35rem 0.75rem',
    fontWeight: 800,
    fontSize: '0.76rem',
    cursor: 'pointer',
  },

  checklistButtonOpen: {
    background: 'rgba(85,199,255,0.08)',
    border:
      '1px solid rgba(85,199,255,0.28)',
  },

  checkIconComplete: {
    color: '#8cff8c',
    fontSize: '1rem',
    fontWeight: 900,
  },

  checkIconIncomplete: {
    color: '#ffd27a',
    fontSize: '1rem',
    fontWeight: 900,
  },

  checkIconWarning: {
    color: '#ffd27a',
    fontSize: '1rem',
    fontWeight: 900,
  },

  checkIconProblem: {
    color: '#ff8d8d',
    fontSize: '1rem',
    fontWeight: 900,
  },

  checkText: {
    display: 'grid',
    alignContent: 'center',
    minWidth: 0,
    gap: '0.15rem',
  },

  checkTitle: {
    color: '#f3f8ff',
    fontSize: '0.94rem',
    fontWeight: 850,
  },

  checkDetail: {
    color: '#8db7e6',
    fontSize: '0.78rem',
    fontWeight: 700,
  },

  expandedArea: {
    marginTop: '0.9rem',
  },

  panel: {
    background: 'rgba(10, 26, 43, 0.42)',
    border:
      '1px solid rgba(120,180,255,0.14)',
    borderRadius: 16,
    padding: '0.9rem',
    display: 'grid',
    gap: '0.9rem',
  },

  panelHeader: {
    marginBottom: '0.35rem',
  },

  panelTitle: {
    color: '#f3f8ff',
    margin: '0.25rem 0 0',
    fontSize: '1rem',
    fontWeight: 850,
  },

  panelAcknowledgedPill: {
    marginTop: '0.4rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    color: '#ffd27a',
    background: 'rgba(255,210,122,0.10)',
    border:
      '1px solid rgba(255,210,122,0.30)',
    borderRadius: 999,
    padding: '0.18rem 0.52rem',
    fontSize: '0.68rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  panelDescription: {
    color: '#8db7e6',
    margin: '0.35rem 0 0',
    fontSize: '0.86rem',
    lineHeight: 1.45,
  },

  panelBody: {
    display: 'grid',
    gap: '0.75rem',
  },

  actionRow: {
    display: 'flex',
    gap: '0.65rem',
    flexWrap: 'wrap',
  },

  completeActionRow: {
    marginTop: '1rem',
    display: 'grid',
    gap: '0.5rem',
    justifyItems: 'start',
  },

  completeActionHint: {
    color: '#8db7e6',
    fontSize: '0.78rem',
    fontWeight: 700,
  },

  muted: {
    color: '#8db7e6',
    fontSize: '0.9rem',
    whiteSpace: 'pre-line',
  },

  ownerActionBanner: {
    marginBottom: '0.9rem',
    border:
      '1px solid rgba(255,210,122,0.32)',
    background:
      'rgba(255,210,122,0.10)',
    borderRadius: 12,
    padding: '0.75rem 0.85rem',
  },

  ownerActionBannerTitle: {
    color: '#ffd27a',
    fontSize: '0.78rem',
    fontWeight: 900,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  ownerActionBannerText: {
    color: '#f8dfb0',
    marginTop: 4,
    fontSize: '0.84rem',
    lineHeight: 1.4,
    fontWeight: 700,
  },

  groundingBanner: {
    marginBottom: '0.9rem',
    border:
      '1px solid rgba(255,120,120,0.35)',
    background:
      'rgba(255,120,120,0.10)',
    borderRadius: 12,
    padding: '0.75rem 0.85rem',
    color: '#ffb5b5',
    fontSize: '0.84rem',
    lineHeight: 1.4,
    fontWeight: 700,
  },

  documentWarningList: {
    display: 'grid',
    gap: 6,
  },

  documentProblem: {
    color: '#ff9c9c',
    fontSize: '0.86rem',
    fontWeight: 700,
  },

  documentWarning: {
    color: '#ffd27a',
    fontSize: '0.86rem',
    fontWeight: 700,
  },

  documentAcknowledged: {
    color: '#ffd27a',
    fontSize: '0.86rem',
    fontWeight: 700,
    border: '1px solid rgba(255,210,122,0.3)',
    borderRadius: 10,
    padding: '0.65rem',
    background: 'rgba(255,210,122,0.08)',
    display: 'grid',
    gap: '0.45rem',
  },

  documentWarningTitle: {
    fontWeight: 800,
    color: '#ffd27a',
  },

  documentWarningMessage: {
    color: '#ffb0b0',
    fontSize: '0.82rem',
    lineHeight: 1.35,
  },

  documentAckLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.45rem',
    color: '#d7ebff',
    fontSize: '0.8rem',
    lineHeight: 1.35,
  },
};

function areDocumentWarningsAcknowledged(
  warnings,
  acknowledgements
) {
  if (!Array.isArray(warnings) || warnings.length === 0) {
    return true;
  }

  return warnings.every((warning) => {
    const record = acknowledgements?.[warning.key];
    return record?.acknowledged === true;
  });
}

function getDocumentActionKey(bookingId, documentType) {
  return `DOC_ACTION|${String(bookingId || '')}|${String(documentType || '').toUpperCase()}`;
}

function getWeightBalanceActionKey(
  bookingId,
  aircraftId
) {
  return `WB_ACTION|${String(bookingId || '')}|${String(aircraftId || '')}`;
}